import { useCallback, useState } from "react";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import type { InputBarFocusedAgent } from "@/components/panels/input-bar-model-pill";
import { runtimeIdForSource } from "@/lib/denker-composer-agent";
import { getModelEffortConfig, isRuntimeKey } from "@/lib/model-registry";
import { metadataString } from "./conversation-frame-utils";

/** Saved chats retain their runtime; model choices open a separate conversation. */
export function useConversationFrameModel(
  agent: InputBarFocusedAgent | null,
  metadata: Record<string, unknown>,
  conversationId: string | undefined,
  startNewConversation: boolean,
  updateModel: CursorInputContextValue["onUpdateAgentModel"] | undefined,
  updateEffort?: CursorInputContextValue["onUpdateAgentEffort"],
  frameId?: string,
) {
  const savedModel = metadataString(metadata, "sessionModel");
  const [selection, setSelection] = useState<{
    conversationId: string | undefined;
    agentId: string;
    model: string;
    adapterType: InputBarFocusedAgent["adapterType"];
    runtimeId: string;
    savedModel?: string;
    effort?: InputBarFocusedAgent["effort"];
  } | null>(null);
  const onUpdateAgentModel = useCallback<
    CursorInputContextValue["onUpdateAgentModel"]
  >(
    async (agentId, adapterType, runtimeId, model) => {
      if (!updateModel) return;
      await updateModel(agentId, adapterType, runtimeId, model, {
        conversationFrameId: frameId,
      });
    },
    [updateModel, frameId],
  );
  const savedAdapter = metadataString(metadata, "sessionAdapterType");
  const savedSource = metadataString(metadata, "sessionRuntimeSource");
  const explicitSelection =
    !startNewConversation &&
    selection?.savedModel === savedModel &&
    selection?.conversationId === conversationId &&
    selection?.agentId === agent?.id
      ? selection
      : null;
  const useSaved =
    agent &&
    !startNewConversation &&
    conversationId &&
    savedModel &&
    (!metadataString(metadata, "projectAgentId", "project_agent_id") ||
      metadataString(metadata, "projectAgentId", "project_agent_id") ===
        agent.id);
  const runtime =
    explicitSelection ??
    (useSaved
      ? {
          model: savedModel,
          adapterType:
            savedAdapter && isRuntimeKey(savedAdapter)
              ? savedAdapter
              : agent.adapterType,
          runtimeId:
            savedAdapter && isRuntimeKey(savedAdapter)
              ? runtimeIdForSource(savedAdapter, savedSource)
              : agent.runtimeId,
        }
      : agent);
  const runtimeModel = runtime?.model;
  const runtimeAdapter = runtime?.adapterType;
  const runtimeId = runtime?.runtimeId;
  const agentModel = agent?.model;
  const agentAdapter = agent?.adapterType;
  const agentRuntimeId = agent?.runtimeId;
  const onUpdateAgentEffort = useCallback<
    CursorInputContextValue["onUpdateAgentEffort"]
  >(
    async (agentId, effort) => {
      if (!runtimeModel || !runtimeAdapter || !updateEffort) return;
      if (
        runtimeModel !== agentModel ||
        runtimeAdapter !== agentAdapter ||
        runtimeId !== agentRuntimeId
      ) {
        if (!updateModel) return;
        await updateModel(
          agentId,
          runtimeAdapter,
          runtimeId ?? runtimeAdapter,
          runtimeModel,
          { startNewConversation: false },
        );
      }
      await updateEffort(agentId, effort);
      setSelection({
        conversationId,
        agentId,
        adapterType: runtimeAdapter,
        runtimeId: runtimeId ?? runtimeAdapter,
        model: runtimeModel,
        savedModel,
        effort,
      });
    },
    [
      agentAdapter,
      agentModel,
      agentRuntimeId,
      conversationId,
      runtimeAdapter,
      runtimeId,
      runtimeModel,
      savedModel,
      updateEffort,
      updateModel,
    ],
  );
  if (!agent || !runtime)
    return { focusedAgent: null, onUpdateAgentModel, onUpdateAgentEffort };
  const effortConfig = getModelEffortConfig(runtime.model);
  const rawEffort =
    explicitSelection?.effort ??
    (useSaved && !explicitSelection
      ? metadataString(metadata, "sessionThinkingEffort")
      : agent.effort);
  const effort: InputBarFocusedAgent["effort"] =
    effortConfig.values.find((value) => value === rawEffort) ?? "default";
  return {
    focusedAgent: { ...agent, ...runtime, effort },
    onUpdateAgentModel,
    onUpdateAgentEffort,
  };
}
